import type { ReactNode } from 'react'
import { ArrowLeft, Check, Home, Plus, Settings, Store, X } from 'lucide-react'
import type { Language } from '../../models'

export const copy = (language: Language, en: string, sv: string) => language === 'sv' ? sv : en
export const errorMessage = (error: unknown) => error instanceof Error ? error.message : String(error)

export function Screen({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <main className={`parity-screen ${className}`}>{children}</main>
}

export function TopBar({
  title,
  subtitle,
  back,
  actions,
}: {
  title: string
  subtitle?: string
  back?: () => void
  actions?: ReactNode
}) {
  return <header className="top-app-bar">
    {back && <IconButton label="Back" onClick={back}><ArrowLeft /></IconButton>}
    <div className="top-title"><strong>{title}</strong>{subtitle && <small>{subtitle}</small>}</div>
    <div className="top-actions">{actions}</div>
  </header>
}

export function BottomNav({
  active,
  navigate,
  language,
}: {
  active: 'home' | 'stores' | 'settings'
  navigate: (path: string) => void
  language: Language
}) {
  return <nav className="bottom-nav" aria-label={copy(language, 'Main navigation', 'Huvudnavigering')}>
    <NavItem active={active === 'home'} icon={<Home />} label={copy(language, 'Lists', 'Listor')} onClick={() => navigate('/pwa/')} />
    <NavItem active={active === 'stores'} icon={<Store />} label={copy(language, 'Stores', 'Butiker')} onClick={() => navigate('/pwa/stores')} />
    <NavItem active={active === 'settings'} icon={<Settings />} label={copy(language, 'Settings', 'Inställningar')} onClick={() => navigate('/pwa/settings')} />
  </nav>
}

function NavItem({ active, icon, label, onClick }: { active: boolean; icon: ReactNode; label: string; onClick: () => void }) {
  return <button type="button" className={active ? 'active' : ''} aria-current={active ? 'page' : undefined} onClick={onClick}>{icon}<span>{label}</span></button>
}

export function IconButton({
  children,
  onClick,
  label,
  className = '',
}: {
  children: ReactNode
  onClick: () => void
  label: string
  className?: string
}) {
  return <button type="button" className={`icon-action ${className}`} aria-label={label} title={label} onClick={onClick}>{children}</button>
}

export function FilledButton({ children, onClick, disabled = false, className = '' }: { children: ReactNode; onClick: () => void; disabled?: boolean; className?: string }) {
  return <button type="button" className={`m3-button filled ${className}`} disabled={disabled} onClick={onClick}>{children}</button>
}

export function OutlinedButton({ children, onClick, disabled = false, className = '' }: { children: ReactNode; onClick: () => void; disabled?: boolean; className?: string }) {
  return <button type="button" className={`m3-button outlined ${className}`} disabled={disabled} onClick={onClick}>{children}</button>
}

export function TextButton({ children, onClick, disabled = false, className = '' }: { children: ReactNode; onClick: () => void; disabled?: boolean; className?: string }) {
  return <button type="button" className={`m3-button text ${className}`} disabled={disabled} onClick={onClick}>{children}</button>
}

export function Chip({ selected, onClick, children, disabled = false }: { selected: boolean; onClick: () => void; children: ReactNode; disabled?: boolean }) {
  return <button type="button" className={`filter-chip ${selected ? 'selected' : ''}`} disabled={disabled} aria-pressed={selected} onClick={onClick}>{selected && <Check />}{children}</button>
}

export function Card({ children, onClick, className = '' }: { children: ReactNode; onClick?: () => void; className?: string }) {
  return onClick
    ? <button type="button" className={`m3-card clickable ${className}`} onClick={onClick}>{children}</button>
    : <section className={`m3-card ${className}`}>{children}</section>
}

export function Notice({ children, error = false }: { children: ReactNode; error?: boolean }) {
  return <div className={`parity-notice ${error ? 'error' : ''}`} role={error ? 'alert' : 'status'}>{children}</div>
}

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return <label className="outlined-field"><span>{label}</span>{children}{hint && <small>{hint}</small>}</label>
}

export function Modal({ title, onClose, children, className = '' }: { title: string; onClose: () => void; children: ReactNode; className?: string }) {
  return <div className="modal-scrim" role="presentation" onPointerDown={(event) => { if (event.currentTarget === event.target) onClose() }}>
    <div className={`m3-dialog ${className}`} role="dialog" aria-modal="true" aria-label={title}>
      <div className="dialog-title"><h2>{title}</h2><IconButton label="Close" onClick={onClose}><X /></IconButton></div>
      {children}
    </div>
  </div>
}

export function Fab({ label, onClick }: { label: string; onClick: () => void }) {
  return <button type="button" className="extended-fab" onClick={onClick}><Plus />{label}</button>
}
